import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Paper,
} from '@mui/material';
import { Event as EventIcon, Settings as SettingsIcon, Warning as WarningIcon, StorefrontOutlined as StorefrontIcon } from '@mui/icons-material';
import { useNavigate, useOutletContext } from 'react-router-dom';

import SMBSelector from '../components/SMBSelector';
import { useSMBContext } from '../contexts/SMBContext';
import { useNotification } from '../contexts/NotificationContext';
import { BookingProvider, useBookingContext } from '../contexts/BookingContext';

import { BookingConfigEditor } from '../features/BookingConfiguration/BookingConfigEditor';
import { CalendarView } from '../features/AppointmentCalendar/CalendarView';
import PageBanner from '../components/PageBanner';
import EmptyState from '../components/EmptyState';
import { BookingsSkeleton } from '../components/skeletons';

const BookingsContent: React.FC = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext<{ smbNavState?: { isOnboarded: boolean; hasSubscription: boolean } } | null>()
  const hasSubscription = outletContext?.smbNavState?.hasSubscription
  const { showNotification } = useNotification();
  const { smbs, selectedSMBId, setSelectedSMBId } = useSMBContext()
  const { 
    activeTab, 
    setActiveTab, 
    smbId, 
    setSmbId, 
    config, 
    isNewConfig, 
    isLoading: configLoading,
    fetchConfig
  } = useBookingContext();

  useEffect(() => {
    if (selectedSMBId && selectedSMBId !== smbId) {
      setSmbId(selectedSMBId)
    }
  }, [selectedSMBId, smbId, setSmbId])
  
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<number | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const isCalendarTabDisabled = !configLoading && (isNewConfig || !config);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchConfig();
      window.dispatchEvent(new CustomEvent('refresh-appointments'));
    } finally {
      setIsRefreshing(false);
    }
  };

  // Resolve initial tab selection based on config
  useEffect(() => {
    if (!configLoading && isInitialLoad && smbId) {
      if (isNewConfig) {
        setActiveTab(1);
      } else {
        setActiveTab(0);
      }
      setIsInitialLoad(false);
    }
  }, [configLoading, isNewConfig, isInitialLoad, smbId, setActiveTab]);

  // Handle switching SMBs - reset initial load state to prevent jitter
  useEffect(() => {
    if (smbId) {
      setIsInitialLoad(true);
    }
    setIsDirty(false);
  }, [smbId]);

  const handleDirtyStateChange = (dirty: boolean) => {
    setIsDirty(dirty);
  };

  const handleTabChange = (newTab: number) => {
    // Check if trying to leave config tab with unsaved changes
    if (activeTab === 1 && isDirty && newTab !== 1) {
      setPendingTab(newTab);
      setShowUnsavedDialog(true);
      return;
    }

    if (newTab === 0 && isCalendarTabDisabled) {
      showNotification('Setup Required: Please configure and save your booking settings first.', 'info');
      return;
    }
    setActiveTab(newTab);
  };

  const confirmTabChange = () => {
    if (pendingTab !== null) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
    setShowUnsavedDialog(false);
    setIsDirty(false);
  };

  const cancelTabChange = () => {
    setPendingTab(null);
    setShowUnsavedDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <PageBanner
        title="Bookings"
        subtitle="Manage your appointments and booking configurations"
        onRefresh={handleRefresh}
        refreshing={isRefreshing || (isInitialLoad && configLoading)}
      />

      <SMBSelector
        smbs={smbs}
        selectedSMBId={selectedSMBId}
        onChange={setSelectedSMBId}
      />

      {isInitialLoad ? (
        <BookingsSkeleton />
      ) : (smbs.length === 0 || (smbs.length === 1 && !smbs[0].onboarding_completed) || (smbId && !smbs.find((s) => s.id === smbId)?.onboarding_completed)) ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, bgcolor: 'background.paper', borderColor: 'divider' }}>
          {hasSubscription === false ? (
            <EmptyState
              icon={<StorefrontIcon />}
              title="Subscription Required"
              description="An active plan with AI replies is required to access Bookings. Choose a plan to manage automated customer appointments."
              action={
                <Button variant="contained" onClick={() => navigate('/dashboard')}>
                  Choose Plan
                </Button>
              }
              minHeight={360}
            />
          ) : (
            <EmptyState
              icon={<StorefrontIcon />}
              title="Add a Business to Get Started"
              description="You haven't added a business yet. Connect your WhatsApp business to manage appointments."
              action={
                <Button variant="contained" onClick={() => navigate('/dashboard')}>
                  Add Business
                </Button>
              }
              minHeight={360}
            />
          )}
        </Paper>
      ) : !smbId ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, bgcolor: 'background.paper', borderColor: 'divider' }}>
          <EmptyState
            icon={<StorefrontIcon />}
            title="No Business Selected"
            description="Please select a business from the dropdown above to view calendar appointments."
            minHeight={360}
          />
        </Paper>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={(_, val) => handleTabChange(val)}
            >
              <Tab 
                icon={<EventIcon />} 
                label="Appointments Calendar" 
                disabled={isCalendarTabDisabled}
                sx={{
                  opacity: isCalendarTabDisabled ? 0.5 : 1,
                }}
              />
              <Tab icon={<SettingsIcon />} label="Booking Configuration" />
            </Tabs>
          </Box>
 
          {/* Calendar Tab */}
          {activeTab === 0 && (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%', minHeight: 0 }}>
              <CalendarView smbId={smbId} />
            </Box>
          )}
 
          {/* Configuration Tab */}
          {activeTab === 1 && (
            <Box>
              <BookingConfigEditor smbId={smbId} onDirtyStateChange={handleDirtyStateChange} />
            </Box>
          )}

          {/* Unsaved Changes Confirmation Dialog */}
          <Dialog
            open={showUnsavedDialog}
            onClose={cancelTabChange}
            aria-labelledby="unsaved-dialog-title"
            PaperProps={{
              sx: { borderRadius: 3, p: 1, maxWidth: 500 }
            }}
          >
            <DialogTitle id="unsaved-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 600 }}>
              <WarningIcon color="warning" />
              Unsaved Changes
            </DialogTitle>
            <DialogContent>
              <DialogContentText variant="body1" color="text.primary">
                You have unsaved changes in the booking configuration. If you leave this tab, your changes will be lost.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={cancelTabChange} variant="contained" sx={{ borderRadius: '20px', px: 3, textTransform: 'none' }}>
                Stay
              </Button>
              <Button onClick={confirmTabChange} color="error" sx={{ textTransform: 'none' }}>
                Leave & Discard
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

const Bookings: React.FC = () => {
  return (
    <BookingProvider>
      <BookingsContent />
    </BookingProvider>
  );
};

export default Bookings;
